import { useForm } from "@refinedev/core";
import { useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ContentTagsField } from "@/components/shared";
import { FormField, FormStickyActions, FormHeader, FormSpacer } from "@/components/forms";
import {
  type TelegramChannel,
  type TelegramChannelFormData,
  type TelegramChatType,
  CHAT_TYPE_OPTIONS,
} from "./types";

/**
 * TelegramChannelsForm
 *
 * Форма управления telegram-channels
 *
 * @returns {React.ReactElement} React компонент
 */
export function TelegramChannelsForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const { formLoading, onFinish, query } = useForm<TelegramChannel>({
    resource: "telegram-channels",
    action: isEdit ? "edit" : "create",
    id,
    redirect: "list",
  });

  const channel = query?.data?.data;
  const initialState = buildTelegramChannelFormState(channel);
  const formKey = isEdit ? `edit-${channel?.id ?? "loading"}` : "create";

  return (
    <TelegramChannelsFormFields
      key={formKey}
      isEdit={isEdit}
      formLoading={formLoading}
      initialState={initialState}
      onFinish={async (values) => {
        await onFinish(values);
      }}
      onBack={() => navigate(-1)}
      channelId={channel?.id ? Number(channel.id) : null}
    />
  );
}

interface TelegramChannelFormState {
  telegramId: string;
  title: string;
  isActive: boolean;
  type: TelegramChatType;
  joinRequestLink: string;
}

function buildTelegramChannelFormState(
  channel?: TelegramChannel
): TelegramChannelFormState {
  const baseState: TelegramChannelFormState = {
    telegramId: "",
    title: "",
    isActive: true,
    type: "channel",
    joinRequestLink: "",
  };

  if (!channel) {
    return baseState;
  }

  return {
    ...baseState,
    telegramId: channel.telegramId,
    title: channel.title,
    isActive: channel.isActive,
    type: channel.type ?? "channel",
    joinRequestLink: channel.joinRequestLink ?? "",
  };
}

interface TelegramChannelsFormFieldsProps {
  isEdit: boolean;
  formLoading: boolean;
  initialState: TelegramChannelFormState;
  onFinish: (values: TelegramChannelFormData) => Promise<void>;
  onBack: () => void;
  channelId: number | null;
}

function TelegramChannelsFormFields({
  isEdit,
  formLoading,
  initialState,
  onFinish,
  onBack,
  channelId,
}: TelegramChannelsFormFieldsProps) {
  const [telegramId, setTelegramId] = useState(initialState.telegramId);
  const [title, setTitle] = useState(initialState.title);
  const [isActive, setIsActive] = useState(initialState.isActive);
  const [type, setType] = useState<TelegramChatType>(initialState.type);
  const [joinRequestLink, setJoinRequestLink] = useState(initialState.joinRequestLink);

  const handleSubmit = async () => {
    const formData: TelegramChannelFormData = {
      telegramId,
      title,
      isActive,
      type,
      joinRequestLink: joinRequestLink || null,
    };
    await onFinish(formData);
  };

  const getTypeHint = () => {
    switch (type) {
      case "channel":
        return "Односторонний канал для публикации контента (пользователи не могут отвечать)";
      case "group":
        return "Обычная группа до 200 участников с возможностью общения";
      case "supergroup":
        return "Супергруппа до 200,000 участников с расширенными возможностями";
      default:
        return "";
    }
  };

  return (
    <div className="space-y-6">
      <FormHeader
        title={isEdit ? "Редактировать канал/чат" : "Добавить канал/чат"}
        onBack={onBack}
        sticky
      />

      <div className="max-w-xl">
        <Card>
          <CardHeader>
            <CardTitle>Telegram канал или чат</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField label="Тип" name="type" required hint={getTypeHint()}>
              <Select
                value={type}
                onValueChange={(v) => setType(v as TelegramChatType)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Выберите тип" />
                </SelectTrigger>
                <SelectContent>
                  {CHAT_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>

            <FormField
              label="Telegram ID"
              name="telegramId"
              required
              hint="ID канала/чата (например: -1001234567890)"
            >
              <Input
                value={telegramId}
                onChange={(e) => setTelegramId(e.target.value)}
                placeholder="-1001234567890"
              />
            </FormField>

            <FormField label="Название" name="title" required>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Название канала или чата"
              />
            </FormField>

            <FormField
              label="Join Request ссылка"
              name="joinRequestLink"
              hint="Ссылка для отправки заявок на вступление (creates_join_request: true). Если не указана, будет создана автоматически при первом приглашении."
            >
              <Input
                value={joinRequestLink}
                onChange={(e) => setJoinRequestLink(e.target.value)}
                placeholder="https://t.me/+XXXXX"
              />
            </FormField>

            <div className="flex items-center space-x-2 pt-2">
              <Checkbox
                id="isActive"
                checked={isActive}
                onCheckedChange={(c) => setIsActive(c === true)}
              />
              <label htmlFor="isActive" className="text-sm font-medium">
                Активен
              </label>
            </div>
          </CardContent>
        </Card>
      </div>

      <FormSpacer />

      {isEdit && channelId !== null ? (
        <div className="rounded-lg border bg-card p-4 mb-4">
          <ContentTagsField entityType="TELEGRAM_CHANNEL" entityId={channelId} />
        </div>
      ) : null}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <FormStickyActions
          primaryLabel={isEdit ? "Сохранить" : "Создать"}
          secondaryLabel="Отмена"
          onPrimary={handleSubmit}
          onSecondary={onBack}
          isPending={formLoading}
        />
      </form>
    </div>
  );
}
